import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactDOM from 'react-dom';
import { Popconfirm, Modal } from 'antd';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './Home.css';
import { defaultNavItems } from '../config/defaultData';
import WallpaperSelector from '../components/WallpaperSelector';
import WebsiteContainerPopup from '../components/popup/WebsiteContainerPopup';
import WebsitePopup from '../components/popup/WebsitePopup';
import FontIcon from '../components/common/FontIcon';
import SortableAppItem from '../components/common/SortableAppItem';
import languageManager from '../utils/languageManager';

// 引入 Redux action
import { 
  fetchNavWebsites, 
  updateNavWebsites,
  deleteWebsite,
  addWebsite,
  updateWebsite,
} from '../redux/slices/websiteSlice';
import logger from '../utils/logger';
import storage from '../utils/LocalStorageManager';
import { PREFERRED_ICON_TYPE, TypeConverters, DATA_TYPE } from '../config/TypeConfig';
import WebsiteUtils from '../utils/websiteUtils';
import DefaultAddCardUtils from '../utils/DefaultAddCardUtils';


import AddAppItem from '../components/AddAppItem';

const ResponsiveGridLayout = WidthProvider(Responsive);
const Home = ({ category }) => {
  const dispatch = useDispatch();
  const categoryId = category || 'home';
  // 使用新的数据结构从 Redux store 获取网站数据
  const websites = useSelector(state => state.website.websitesByNav[categoryId] || []);
  // 获取导航项列表，用于检查当前分类是否还存在
  const navItems = useSelector(state => state.nav.navItems || []);
  // 获取设置状态
  const { iconSpacing, iconSize, iconRadius, iconTextColor, websiteDisplayMode } = useSelector(state => state.settings);
  // 使用 ref 来跟踪是否已经加载过数据
  const hasLoadedRef = useRef(false);
  const [apps, setApps] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [activeContextMenu, setActiveContextMenu] = useState(null);
  const [showBackgroundMenu, setShowBackgroundMenu] = useState(false);
  const [backgroundMenuPosition, setBackgroundMenuPosition] = useState({ x: 0, y: 0 });
  const [isBatchEditing, setIsBatchEditing] = useState(false); 
  const [showWallpaperPopup, setShowWallpaperPopup] = useState(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [appToDelete, setAppToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const backgroundMenuRef = useRef(null);
  
  // 布局相关状态
  const [layout, setLayout] = useState([]);
  
  // 动态计算列数
  const [gridCols, setGridCols] = useState(12);
  const gridContainerRef = useRef(null);

  // 生成复合ID的工具函数
  const generateComplexId = (app) => {
    return `${app.url || ''}_${app.name || ''}_${app.id || Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  };

  // 确保应用数据有唯一复合ID的工具函数
  const ensureUniqueComplexIds = (apps) => {
    // 检查应用是否都有唯一复合ID，如果没有则添加
    const updatedApps = apps.map(app => {
      if (!app.complexId) {
        return { ...app, complexId: generateComplexId(app) };
      }
      return app;
    });
    
    // 检查是否有ID重复问题
    const ids = updatedApps.map(app => app.complexId);
    const hasDuplicates = ids.length !== new Set(ids).size;
    
    // 如果有重复，生成新的唯一ID
    if (hasDuplicates) {
      const fixedApps = updatedApps.map(app => ({
        ...app,
        complexId: generateComplexId(app)
      }));
      
      logger.log('[Home] 发现ID重复，生成新ID后的应用ID顺序:', fixedApps.map(app => app.complexId));
      return fixedApps;
    }
    
    return updatedApps;
  };

  // 根据应用数据动态生成布局 - 支持智能碰撞检测和自动换行
  const generateLayout = (appsData) => {
    const layout = [];
    const cols = gridCols; // 使用动态列数
    
    // 使用二维数组来跟踪已占用的网格位置
    const grid = Array(20).fill(null).map(() => Array(cols).fill(false));
    
    appsData.forEach((app) => {
      // 根据 dataType 确定网格占用大小
      let itemWidth = 1;
      let itemHeight = 1;
      
      if (app.dataType === DATA_TYPE.HOT_SEARCH) {
        // 详情模式占用3列，其他模式占用4列
        itemWidth = websiteDisplayMode === 'detailed' ? 2 : 4;
        itemHeight = 2; // 占用2行
      } else if (app.dataType === DATA_TYPE.DAILY_ENGLISH) {
        // 每日英语卡片占用2列2行
        itemWidth = websiteDisplayMode === 'detailed' ? 2 : 4;
        itemHeight = 2;
      }
      
      // 找到第一个可用的位置
      let found = false;
      let itemX = 0;
      let itemY = 0;
      
      for (let y = 0; y < grid.length && !found; y++) {
        for (let x = 0; x <= cols - itemWidth && !found; x++) {
          // 检查这个位置是否有足够的空间
          let canPlace = true;
          for (let dy = 0; dy < itemHeight && canPlace; dy++) {
            for (let dx = 0; dx < itemWidth && canPlace; dx++) {
              if (y + dy >= grid.length || x + dx >= cols || grid[y + dy][x + dx]) {
                canPlace = false;
              }
            }
          }
          
          if (canPlace) {
            itemX = x;
            itemY = y;
            found = true;
            
            // 标记这个位置为已占用
            for (let dy = 0; dy < itemHeight; dy++) {
              for (let dx = 0; dx < itemWidth; dx++) {
                if (y + dy < grid.length && x + dx < cols) {
                  grid[y + dy][x + dx] = true;
                }
              }
            }
          }
        }
      }
      
      if (found) {
        layout.push({
          i: app.complexId || app.id,
          x: itemX,
          y: itemY,
          w: itemWidth,
          h: itemHeight,
          static: false
        });
      } else {
        // 如果找不到合适的位置，放在最后一行
        logger.warn(`[generateLayout] 无法为 ${app.name} 找到合适位置，放在最后一行`);
        layout.push({
          i: app.complexId || app.id,
          x: 0,
          y: Math.max(...layout.map(item => item.y + item.h), 0),
          w: itemWidth,
          h: itemHeight,
          static: false
        });
      }
    });

    // 添加按钮跟随在最后一个item后面，除非一行已满才到下一行
    if (layout.length > 0) {
      // 找到最后一个item的位置和尺寸
      const lastItem = layout[layout.length - 1];
      const lastItemEndX = lastItem.x + lastItem.w;
      const lastItemY = lastItem.y;
      
      // 检查当前行是否还有空间
      if (lastItemEndX + 1 <= cols) {
        // 当前行还有空间，放在最后一个item后面
        layout.push({
          i: 'add-button',
          x: lastItemEndX,
          y: lastItemY,
          w: 1,
          h: 1,
          static: true // 设置为静态，不参与拖动
        });
      } else {
        // 当前行已满，放到下一行开头
        layout.push({
          i: 'add-button',
          x: 0,
          y: lastItemY + lastItem.h,
          w: 1,
          h: 1,
          static: true // 设置为静态，不参与拖动
        });
      }
    } else {
      // 如果没有其他item，放在(0,0)位置
      layout.push({
        i: 'add-button',
        x: 0,
        y: 0,
        w: 1,
        h: 1,
        static: true // 设置为静态，不参与拖动
      });
    }
    
    console.log('generateLayout 布局:', layout);
    return layout;
  };

  // 动态计算网格列数 - 确保能容纳所有item
  const calculateGridCols = useCallback(() => {
    if (!gridContainerRef.current) return 12;
    const containerWidth = gridContainerRef.current.offsetWidth;
    // Detailed mode needs extra 65px width (user's manual change)
    const extraWidth = websiteDisplayMode === 'detailed' ? 70 : 0;
    const itemWidth = iconSize + iconSpacing + 10 + extraWidth; // Icon + spacing + buffer + extra width
    const cols = Math.floor(containerWidth / itemWidth);
    return Math.max(1, Math.min(cols, 20));
  }, [iconSize, iconSpacing, websiteDisplayMode]);

  // 在组件挂载时尝试直接从 storage 加载初始数据，避免依赖 Redux 初始化
  useEffect(() => {
    // 如果已经加载过初始数据，则跳过
    if (hasLoadedInitialData) return;
    
    try {
      logger.log(`[Home-Init] 尝试从 storage 读取初始数据，categoryId: ${categoryId}`);
      
      const websites = storage.getWebsitesForNav(categoryId);
      if (websites && websites.length > 0) {
        logger.log(`[Home-Init] 成功读取到初始数据，应用数量: ${websites.length}`);
        
        // 确保数据有复合ID
        const dataWithComplexIds = ensureUniqueComplexIds(websites);
        
        // 设置初始应用列表
        setApps(dataWithComplexIds);
        
        // 记录已加载初始数据
        setHasLoadedInitialData(true);
        
        logger.log(`[Home-Init] 初始应用ID顺序:`, dataWithComplexIds.map(app => app.complexId));
        
        // 同时更新 Redux 状态，确保一致性
        dispatch(updateNavWebsites({
          navId: categoryId,
          websites: dataWithComplexIds,
          skipSync: true, // 不触发事件，避免循环
          from: 'storage'
        }));
      } else {
        logger.log(`[Home-Init] storage 中没有找到初始数据`);
      }
    } catch (error) {
      logger.error(`[Home-Init] 读取初始数据失败:`, error);
    }
  }, [categoryId, hasLoadedInitialData, dispatch]);
  

        
  // 当 Redux 中的网站数据变化时更新本地状态
  useEffect(() => {
    logger.log(`[Home] Redux数据变更，categoryId: ${categoryId}, 应用数量: ${websites?.length || 0}`);
    
    // 检查当前categoryId对应的导航项是否还存在
    const navItemExists = navItems.some(item => item.id === categoryId);
    if (!navItemExists && categoryId !== 'home') {
      logger.log(`[Home] 导航项 ${categoryId} 已被删除，跳过数据处理`);
      return;
    }
    
    if (websites && websites.length > 0) {
      // 记录应用ID顺序，用于调试
      logger.log('[Home] Redux中的应用ID顺序:', websites.map(app => 
        app.complexId || app.id
      ));
        
      // 确保应用数据有唯一复合ID
      const processedApps = ensureUniqueComplexIds(websites);
      setApps(processedApps);
      
      // 如果处理后的数据与原数据不同，需要更新Redux
      if (JSON.stringify(processedApps) !== JSON.stringify(websites)) {
        // 保存到 Redux 和 storage
        dispatch(updateNavWebsites({ 
          navId: categoryId, 
          websites: processedApps,
          skipSync: true,
          from: 'storage2'
        }));
      }
    } else {
      // 检查本地是否已有数据，避免用默认数据覆盖
      const storedWebsites = storage.getWebsitesForNav(categoryId);
      // 只有当本地存储中没有数据时，才使用默认数据
      if (!storedWebsites) {
        logger.log(`[Home] storage 中没有数据，使用默认数据`);
        
        // 从defaultNavItems中获取对应category的初始数据并添加复合ID
        const defaultCategoryItem = defaultNavItems.find(item => 
          (category ? item.id === category : item.id === 'home')
        );
        
        const defaultApps = defaultCategoryItem ? ensureUniqueComplexIds(defaultCategoryItem.websites) : [];
        
        logger.log('[Home] 使用默认数据，应用数量:', defaultApps.length);
        setApps(defaultApps);
          
        // 保存到 Redux 和 storage
        dispatch(updateNavWebsites({ 
          navId: categoryId, 
          websites: defaultApps,
          skipSync: true,
          from: 'storage3'
        }));
      } else {
        // 如果本地已有数据，处理 Redux 中的空数据是正常的，不需要特别处理
        logger.log(`[Home] Redux数据为空，但本地已有数据，不使用默认数据`);
      }
    }
  }, [categoryId, websites, dispatch, category, navItems]);
  
  // 使用 ref 来跟踪是否已经初始化过默认卡片
  const defaultCardsInitialized = useRef(false);
  
  // 检查并添加默认卡片（热搜卡片等）
  useEffect(() => {
    if (apps.length > 0 && !defaultCardsInitialized.current) {
      const result = DefaultAddCardUtils.checkAndAddDefaultCards(categoryId, apps);
      
      if (result.hasChanges) {
        logger.log('[Home] 检测到默认卡片变化', {
          addedCards: result.addedCards,
          totalApps: result.apps.length
        });
        
        setApps(result.apps);
        
        // 同步到 Redux
        dispatch(updateNavWebsites({
          navId: categoryId,
          websites: result.apps,
          from: 'defaultCards'
        }));
      }
      
      // 标记为已初始化
      defaultCardsInitialized.current = true;
    }
  }, [categoryId, apps, dispatch]);
  
  // 监听图标加载失败事件
  useEffect(() => {
    const handleIconLoadError = (e) => {      if (e && e.detail && e.detail.appId) {
        const appId = e.detail.appId;
        
        // 更新应用列表，将加载失败的图标应用切换为使用文字图标
        setApps(prevApps => {
          const updatedApps = prevApps.map(app => {
            // 使用复合ID或原始ID进行匹配，以便找到正确的应用
            if ((app.complexId && app.complexId === appId) || app.id === appId) {
              // 如果应用没有文字图标，则从应用名称中提取
              let textIcon = app.textIcon;
              if (!textIcon) {
                textIcon = app.name.substring(0, 4);
              }
              
              return {
                ...app,
                // 清除图片图标相关属性
                iconUrl: null,
                ico: null,
                // 确保设置了文字图标
                textIcon: textIcon,
                // 设置首选图标类型为"text"，强制使用文字图标
                preferredIconType: PREFERRED_ICON_TYPE.TEXT
              };
            }
            return app;
          });
          
          // 保存更新后的应用列表到 Redux
          dispatch(updateNavWebsites({ 
            navId: categoryId, 
            websites: updatedApps,
            skipSync: true ,
            from: 'iconLoadError'
          }));
          
          return updatedApps;
        });
      }
    };
    
    window.addEventListener('iconLoadError', handleIconLoadError);
    
    return () => {
      window.removeEventListener('iconLoadError', handleIconLoadError);
    };
  }, [categoryId, dispatch]);
  
  // 处理拖动停止事件 - 实现真正的交换逻辑，对齐 GridDemo.js
  const handleDragStop = (layout, oldItem, newItem, placeholder, event, element) => {
    
    logger.log('handleDragStop 拖动停止事件:', {
      layout,
      oldItem,
      newItem,
      placeholder
    });
    
    // 如果没有拖动项目或新项目，直接返回
    if (!oldItem || !newItem) {
      logger.log('handleDragStop 跳过：缺少拖动项目信息');
      return;
    }
    
    // 检查位置是否真的发生了变化
    if (oldItem.x === newItem.x && oldItem.y === newItem.y) {
      logger.log('handleDragStop 跳过：位置没有变化');
      return;
    }
    
    logger.log('handleDragStop 位置发生变化:', {
      from: { x: oldItem.x, y: oldItem.y },
      to: { x: newItem.x, y: newItem.y }
    });
    
    
    // 找到目标位置是否有其他item（基于新的坐标）
    const targetX = newItem.x;
    const targetY = newItem.y;
    
    // 在布局中找到占用目标位置的item
    const targetItem = layout.find(item => 
      item.i !== newItem.i && 
      item.x === targetX && 
      item.y === targetY
    );
    
    if (targetItem) {
      logger.log('handleDragStop 目标位置item:', targetItem.i, '将与', newItem.i, '交换');
      
      // 在apps中找到对应的索引并交换
      const newApps = [...apps];
      const draggedIndex = newApps.findIndex(app => 
        (app.complexId && app.complexId === newItem.i) || 
        (app.id === newItem.i)
      );
      const targetIndex = newApps.findIndex(app => 
        (app.complexId && app.complexId === targetItem.i) || 
        (app.id === targetItem.i)
      );
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        logger.log('handleDragStop 执行数据交换:', draggedIndex, '↔', targetIndex);
        
        // 交换两个item的位置
        [newApps[draggedIndex], newApps[targetIndex]] = 
        [newApps[targetIndex], newApps[draggedIndex]];
        
        logger.log('handleDragStop 交换后的应用数据:', newApps.slice(0, 5).map(app => app.name));
        
        setApps(newApps);
        
        // 同步到 Redux 和 storage
        dispatch(updateNavWebsites({
          navId: categoryId,
          websites: newApps,
          from: 'drag-drop'
        }));
        
      }
    } else {
      logger.log('handleDragStop 目标位置没有其他item，执行重新排序');
      
      // 如果目标位置没有其他item，根据新位置重新排序
      const sortedLayout = [...layout].sort((a, b) => {
        if (a.y !== b.y) return a.y - b.y;
        return a.x - b.x;
      });
      
      const newOrder = sortedLayout.map(item => {
        const originalItem = apps.find(app => 
          (app.complexId && app.complexId === item.i) || 
          (app.id === item.i)
        );
        return originalItem;
      }).filter(Boolean);
      
      setApps(newOrder);
      
      // 同步到 Redux 和 storage
      dispatch(updateNavWebsites({
        navId: categoryId,
        websites: newOrder,
        from: 'drag-drop-reorder'
      }));
      
    }
  };
  
  // 添加新应用并关闭弹窗
  const addNewAppAndClose = (newApp) => {
    if (newApp) {
      const appWithComplexId = {
        ...newApp,
        complexId: generateComplexId(newApp)
      };
      
      // 使用工具类处理网站插入位置
      setApps(prevApps => WebsiteUtils.insertWebsiteByType(prevApps, appWithComplexId));
      
      setShowModal(false);
      
      // 记录操作日志
      WebsiteUtils.logWebsiteAction('添加', appWithComplexId, categoryId);
      
      // 保存到 Redux 和 storage
      dispatch(addWebsite({ 
        navId: categoryId, 
        website: appWithComplexId,
        from: 'add-modal'
      }));
    }
  };

  // 添加新应用但不关闭弹窗
  const addNewAppNoClose = (newApp) => {
    const appWithComplexId = {
      ...newApp,
      complexId: generateComplexId(newApp)
    };
    
    // 使用工具类处理网站插入位置
    const updatedApps = WebsiteUtils.insertWebsiteByType(apps, appWithComplexId);
    setApps(updatedApps);
    
    // 记录操作日志
    WebsiteUtils.logWebsiteAction('添加', appWithComplexId, categoryId);
    
    dispatch(addWebsite({ navId: categoryId, website: appWithComplexId }));
    // 不关闭弹窗
  };
  
  // 保存编辑后的应用
  const saveEditedApp = (editedApp) => {
    logger.log('[Home] 保存编辑的应用:', {
      editedApp,
      currentApps: apps,
      complexId: editedApp.complexId
    });
    
    // 更新本地状态
    const updatedApps = apps.map(app => {
      if (app.complexId === editedApp.complexId) {
        logger.log('[Home] 找到匹配的应用，进行更新:', app);
        return editedApp;
      }
      return app;
    });
    
    logger.log('[Home] 更新后的应用列表:', updatedApps);
    setApps(updatedApps);
    
    // 使用 updateWebsite action 更新单个网站
    dispatch(updateWebsite({ 
      navId: categoryId, 
      websiteId: editedApp.id || editedApp.complexId,
      updates: editedApp
    }));
    
    // 关闭弹窗
    setShowEditModal(false);
    setEditingApp(null);
  };
  
  // 删除应用
  const handleDeleteApp = useCallback((appId) => {
    // 查找要删除的应用，支持通过复合ID或原始ID查找
    const appToDelete = apps.find(app => 
      app.complexId === appId || app.id === appId
    );
    
    if (!appToDelete) {
      logger.error('找不到要删除的应用:', appId);
      return;
    }
    
    if (isBatchEditing) {
      // 批量编辑模式下直接删除，无需确认
      performDelete(appToDelete);
      return;
    }
    
    // 右键菜单删除时需要确认
    setAppToDelete(appToDelete);
    setShowDeleteConfirm(true);
  }, [apps, categoryId, dispatch, isBatchEditing]);
  
  // 执行删除操作
  const performDelete = useCallback((appToDelete) => {
    const updatedApps = apps.filter(app => 
      app.complexId !== appToDelete.complexId && app.id !== appToDelete.id
    );
    setApps(updatedApps);
    
    // 使用 deleteWebsite action
    dispatch(deleteWebsite({ 
      navId: categoryId, 
      websiteId: appToDelete.id,
      complexId: appToDelete.complexId
    }));
    
    // 触发网站删除事件
    const deleteEvent = new CustomEvent('websiteDelete', {
      detail: {
        id: appToDelete.id,
        complexId: appToDelete.complexId
      }
    });
    window.dispatchEvent(deleteEvent);
  }, [apps, categoryId, dispatch]);
  
  // 确认删除
  const confirmDelete = useCallback(() => {
    if (appToDelete) {
      performDelete(appToDelete);
    }
    setShowDeleteConfirm(false);
    setAppToDelete(null);
  }, [appToDelete, performDelete]);
  
  // 取消删除
  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setAppToDelete(null);
  }, []);
  
  // 确认重置
  const confirmReset = useCallback(() => {
    // 根据当前类别获取对应的默认数据
    const defaultCategoryItem = defaultNavItems.find(item => 
      (category ? item.id === category : item.id === 'home')
    );
    const defaultApps = defaultCategoryItem ? defaultCategoryItem.websites : [];
    
    setApps(defaultApps);
    
    const storageKey = category ? `homeApps_${category}` : 'homeApps';
    storage.setItem(storageKey, JSON.stringify(defaultApps));
    
    setShowResetConfirm(false);
  }, [category]);
  
  // 取消重置
  const cancelReset = useCallback(() => {
    setShowResetConfirm(false);
  }, []);

  const handleAddClick = () => {
    setShowModal(true);
  };

  // 添加对grid-app-delete事件的监听
  useEffect(() => {
    const handleGridAppDelete = (event) => {
      if (event.detail) {
        const appId = event.detail.id || event.detail.complexId;
        if (appId) {
          handleDeleteApp(appId);
        } else {
          logger.error('无法从事件中获取有效的应用ID:', event.detail);
        }
      }
    };

    window.addEventListener('grid-app-delete', handleGridAppDelete);
    
    return () => {
      window.removeEventListener('grid-app-delete', handleGridAppDelete);
    };
  }, [handleDeleteApp]); // 添加 handleDeleteApp 作为依赖


  // 处理背景右键菜单
  const handleBackgroundContextMenu = (e) => {
    e.preventDefault();
    
    // 如果有弹窗正在显示，则不显示右键菜单
    if (showModal || showEditModal || showWallpaperPopup) {
      return;
    }
    
    // 确保点击的是背景区域而不是应用项
    if (!e.target.closest('.app-item') && !e.target.closest('.add-app')) {
      setBackgroundMenuPosition({ x: e.clientX, y: e.clientY });
      setShowBackgroundMenu(true);
      // 关闭其他所有菜单
      setActiveContextMenu(null);
    }
  };

  // 关闭背景右键菜单
  const closeBackgroundMenu = () => {
    setShowBackgroundMenu(false);
  };

  // 处理点击页面其他区域关闭背景菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (backgroundMenuRef.current && !backgroundMenuRef.current.contains(event.target)) {
        closeBackgroundMenu();
      }
    };
    
    if (showBackgroundMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBackgroundMenu]);

  // 处理点击其他区域退出批量编辑模式
  useEffect(() => {
    if (!isBatchEditing) return;
    
    const handleClickOutside = (event) => {
      // 如果点击的不是应用项或删除按钮，则退出批量编辑模式
      if (!event.target.closest('.app-item') && !event.target.closest('.batch-delete-button')) {
        setIsBatchEditing(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBatchEditing]);

  // 监听窗口大小变化和设置变化，重新计算网格列数
  useEffect(() => {
    const handleResize = () => {
      // 重新计算列数
      const newCols = calculateGridCols();
      if (newCols !== gridCols) {
        setGridCols(newCols);
      }
      
      // 触发强制重新渲染，确保应用图标和布局正确更新
      const resizeEvent = new CustomEvent('windowResized', {
        detail: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      });
      window.dispatchEvent(resizeEvent);
    };

    // 初始计算列数
    const newCols = calculateGridCols();
    if (newCols !== gridCols) {
      setGridCols(newCols);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateGridCols, gridCols]);

  // 监听设置变化，重新计算网格列数
  useEffect(() => {
    const newCols = calculateGridCols();
    if (newCols !== gridCols) {
      setGridCols(newCols);
    }
  }, [iconSize, iconSpacing, websiteDisplayMode, calculateGridCols, gridCols]);

  // 当 gridCols 变化时，重新生成布局
  useEffect(() => {
    if (gridCols > 0) {
      const newLayout = generateLayout(apps);
      setLayout(newLayout);
    }
  }, [gridCols, apps]);





  // 编辑应用
  const handleEditApp = (app) => {
    // 保存原始 ID 和复合 ID
    const appToEdit = apps.find(item => 
      (item.complexId && item.complexId === app.complexId) || 
      (item.id === app.id)
    );
    
    if (appToEdit) {
      setEditingApp(appToEdit);
      setShowEditModal(true);
    } else {
      logger.error('找不到要编辑的应用:', app);
    }
  };

  return (
    <div 
      className="home-container" 
      onContextMenu={handleBackgroundContextMenu}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* React Grid Layout 替换原有的拖拽逻辑 */}
      <div className="grid-layout-container" ref={gridContainerRef}>
        <ResponsiveGridLayout
          className="layout"
          layouts={{ lg: layout }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: gridCols, md: gridCols, sm: gridCols, xs: gridCols, xxs: gridCols }}
          rowHeight={websiteDisplayMode === 'detailed' ? iconSize : iconSize + 20}
          margin={[iconSpacing, iconSpacing]}
          onItemClick={(layoutItem, e) => {
            // 点击事件现在由 SortableAppItem 统一处理
            // 这里只处理 react-grid-layout 特有的逻辑
          }}
          onDragStop={handleDragStop}
          isDraggable={true}
          isResizable={false}
          useCSSTransforms={true}
          preventCollision={false}
          isBounded = {true}
          compactType="horizontal"
          autoSize={true}
          horizontalCompact={true}
          transformScale={1}
          draggableHandle=".drag-handle"
        >
          {apps.map((app) => (
            <div key={app.complexId || app.id} className="grid-app-item">
              <SortableAppItem
                app={app}
                onEdit={handleEditApp}
                onDelete={handleDeleteApp}
                activeContextMenu={activeContextMenu}
                setActiveContextMenu={setActiveContextMenu}
                isBatchEditing={isBatchEditing}
                setShowWallpaperPopup={setShowWallpaperPopup}
                iconSize={iconSize}
                iconRadius={iconRadius}
                iconTextColor={iconTextColor}
                // 处理应用点击事件
                onItemClick={(app, e) => {
                  // 处理应用点击
                  if (app.url) {
                    const appData = {
                      id: app.id,
                      complexId: app.complexId,
                      name: app.name,
                      title: app.name,
                      url: app.url,
                      ico: app.ico || app.iconUrl,
                      bgColor: app.bgColor,
                      textIcon: app.textIcon,
                      iconColor: app.iconColor,
                      dataType: app.dataType,
                      preferredIconType: app.preferredIconType
                    };
                    const event = new CustomEvent('websiteClick', { detail: appData });
                    window.dispatchEvent(event);
                  }
                }}
              />
            </div>
          ))}
          
          {/* 添加按钮作为最后一个item */}
          <div key="add-button" className="grid-app-item">
            <AddAppItem 
              onClick={handleAddClick} 
              iconSize={iconSize}
              iconRadius={iconRadius}
              iconTextColor={iconTextColor}
            />
          </div>
        </ResponsiveGridLayout>
        

      </div>

      {showModal && (
        <WebsiteContainerPopup
          onClose={() => setShowModal(false)}
          onSave={addNewAppAndClose}
          onSaveAndContinue={addNewAppNoClose}
        />
      )}
      
      {showEditModal && editingApp && (
        <WebsitePopup
          initialData={editingApp}
          onClose={() => {
            setShowEditModal(false);
            setEditingApp(null);
          }}
          onSave={saveEditedApp}
        />
      )}
      
      {/* 背景右键菜单 */}
      {showBackgroundMenu && ReactDOM.createPortal(
        <div
          ref={backgroundMenuRef}
          className="home-context-menu"
          style={{
            position: 'fixed',
            left: `${backgroundMenuPosition.x}px`,
            top: `${backgroundMenuPosition.y}px`
          }}
        >
          <div 
            className="menu-item"
            onClick={() => {
              setShowModal(true);
              closeBackgroundMenu();
            }}
          >
            <FontIcon name="plus" style={{ width: '20px', marginRight: '8px' }} />
            <span>{languageManager.t('home.addWebsiteNavigation')}</span>
          </div>
          <div 
            className="menu-item"
            onClick={() => {
              setIsBatchEditing(!isBatchEditing);
              closeBackgroundMenu();
            }}
          >
            <FontIcon name="edit" style={{ width: '20px', marginRight: '8px' }} />
            <span>{languageManager.t('home.batchEdit')}</span>
          </div>
          <div 
            className="menu-item"
            onClick={() => {
              setShowWallpaperPopup(true);
              closeBackgroundMenu();
            }}
          >
            <FontIcon name="image" style={{ width: '20px', marginRight: '8px' }} />
            <span>{languageManager.t('home.changeWallpaper')}</span>
          </div>
          <div 
            className="menu-item"
            onClick={() => {
              setShowResetConfirm(true);
              closeBackgroundMenu();
            }}
    
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <FontIcon name="undo" style={{ width: '20px' , marginRight: '8px'}} />
            <span>{languageManager.t('home.resetInitialApps')}</span>
          </div>
          <div 
            className="menu-item"
            onClick={() => {
              window.dispatchEvent(new CustomEvent('showSettingsDrawer'));
              closeBackgroundMenu();
            }}
          >
            <FontIcon name="cog" style={{ width: '20px', marginRight: '8px' }} />
            <span>{languageManager.t('home.settings')}</span>
          </div>
        </div>,
        document.body
      )}
      
      {/* 使用新的WallpaperSelector组件 */}
      <WallpaperSelector 
        visible={showWallpaperPopup}
        onClose={() => setShowWallpaperPopup(false)}
      />
      
      {/* 删除确认弹窗 */}
      <Modal
        title={languageManager.t('home.deleteConfirm')}
        open={showDeleteConfirm}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText={languageManager.t('common.confirm')}
        cancelText={languageManager.t('common.cancel')}
        centered
        zIndex={9999}
      >
        <p>{languageManager.t('home.deleteConfirmMessage')}</p>
      </Modal>
      
      {/* 重置确认弹窗 */}
      <Modal
        title={languageManager.t('home.resetConfirm')}
        open={showResetConfirm}
        onOk={confirmReset}
        onCancel={cancelReset}
        okText={languageManager.t('common.confirm')}
        cancelText={languageManager.t('common.cancel')}
        centered
        zIndex={9999}
      >
        <p>{languageManager.t('home.resetConfirmMessage')}</p>
      </Modal>
    </div>
  );
};

export default Home;